import { useEffect } from 'react';
import { useParams } from 'react-router';
import { useLocalStorage } from 'react-use';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { Flex, Modal } from 'antd';
import { QUEUE_DOCUMENT_DOWNLOAD } from 'mutations/documentDownloader';
import {
  GET_DOWNLOAD_STATUS,
  GET_DOWNLOAD_URL,
} from 'queries/documentDownloader';
import useModalControls from 'hooks/useModalControls';
import CondensedTable from 'components/CondensedTable/CondensedTable';
import { formatDate } from 'utilities/datesAndTimes';
import {
  RequestDownloadButton,
  ViewDownloadAndFailedDocumentsButtons,
} from './StatementDownloadButtons';
import dayjs from 'dayjs';

const DownloadAllStatementsButton = () => {
  const { accountId } = useParams();
  const [downloadRequests, setDownloadRequests] = useLocalStorage(
    'download-requests',
    {},
  );

  const downloadRequestId = downloadRequests?.[accountId]?.requestId;
  const currentDownloadRequest = downloadRequests?.[accountId] || {};
  const downloadComplete = currentDownloadRequest?.downloadComplete || false;

  const failedDocumentsModal = useModalControls();

  const [queueDownload, { loading: queueDownloadLoading }] = useMutation(
    QUEUE_DOCUMENT_DOWNLOAD,
    {
      onCompleted: (data) => {
        setDownloadRequests({
          ...downloadRequests,
          [accountId]: { requestId: data.queueDocumentDownload },
        });
      },
    },
  );

  const { data: downloadStatusData, stopPolling } = useQuery(
    GET_DOWNLOAD_STATUS,
    {
      ...(downloadRequestId && { variables: { requestId: downloadRequestId } }),
      skip: !downloadRequestId,
      pollInterval: 750,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const [getDownloadUrl, { data: downloadUrlData }] =
    useLazyQuery(GET_DOWNLOAD_URL);

  useEffect(() => {
    if (downloadStatusData?.documentRequestStatus?.processedTz) {
      stopPolling();
      setDownloadRequests({
        ...downloadRequests,
        [accountId]: {
          ...downloadRequests[accountId],
          failedDocuments:
            downloadStatusData.documentRequestStatus.failedDocuments,
          processedTz: downloadStatusData.documentRequestStatus.processedTz,
          totalDocuments:
            downloadStatusData.documentRequestStatus.totalDocuments,
        },
      });
      getDownloadUrl({ variables: { requestId: downloadRequestId } });
    }
  }, [downloadStatusData?.documentRequestStatus?.processedTz]);

  useEffect(() => {
    if (downloadRequests?.[accountId]?.processedTz) {
      const processedTime = dayjs(downloadRequests[accountId].processedTz);
      const threeMinutesFromProcessed = processedTime.add(3, 'minute');

      if (dayjs().isAfter(threeMinutesFromProcessed)) {
        const updatedRequests = { ...downloadRequests };
        delete updatedRequests[accountId];
        setDownloadRequests(updatedRequests);
      }
    }
  }, []);

  const handleDownloadComplete = () => {
    setTimeout(() => {
      setDownloadRequests({
        ...downloadRequests,
        [accountId]: {
          ...downloadRequests[accountId],
          downloadComplete: true,
        },
      });
    }, 500);
  };

  useEffect(() => {
    if (
      !downloadComplete &&
      downloadUrlData?.documentDownloadUrl &&
      currentDownloadRequest?.failedDocuments?.length === 0
    ) {
      window.location.href = downloadUrlData.documentDownloadUrl;
      handleDownloadComplete();
    }
  }, [currentDownloadRequest, downloadComplete, downloadUrlData]);

  const requestDownload = () => {
    queueDownload({
      variables: {
        request: {
          accountRequest: {
            accountId: accountId,
            documentTypes: ['STATEMENTS'],
          },
        },
      },
    });
  };

  const closeModal = () => {
    failedDocumentsModal.hide();
  };

  return (
    <>
      {currentDownloadRequest?.failedDocuments?.length > 0 ? (
        <ViewDownloadAndFailedDocumentsButtons
          currentDownloadRequest={currentDownloadRequest}
          downloadUrl={downloadUrlData?.documentDownloadUrl}
          failedDocumentsModal={failedDocumentsModal}
          handleDownloadComplete={handleDownloadComplete}
        />
      ) : (
        <RequestDownloadButton
          currentDownloadRequest={currentDownloadRequest}
          downloadRequestId={downloadRequestId}
          queueDownloadLoading={queueDownloadLoading}
          requestDownload={requestDownload}
        />
      )}

      <Modal
        {...failedDocumentsModal}
        title="Failed Statements"
        footer={null}
        width={600}
        onCancel={closeModal}
      >
        <Flex vertical gap="var(--spacing-lg)">
          {currentDownloadRequest?.failedDocuments?.length > 0 && (
            <CondensedTable
              columns={[
                {
                  title: 'Date',
                  dataIndex: 'date',
                  key: 'date',
                  render: (date) => formatDate(date),
                },
                {
                  title: 'PDF',
                  dataIndex: 'url',
                  key: 'url',
                  render: (text) => (
                    <a href={text} target="_blank" rel="noreferrer">
                      View PDF
                    </a>
                  ),
                },
              ]}
              data={currentDownloadRequest.failedDocuments}
              rowKey={(record) => record.url}
            />
          )}
        </Flex>
      </Modal>
    </>
  );
};

export default DownloadAllStatementsButton;
